import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaSearch, FaSpinner, FaRoute } from 'react-icons/fa';
import ApiService from '../ApiService';
import '../styles/SuggestedRoutes.css';

function estimateDuration(stopCount) {
  return Math.round(stopCount * 6 / 5) * 5;
}

function RouteThumbnail() {
  return (
    <div className="srs-thumbnail">
      <div className="srs-thumbnail-grid" />
      <FaRoute className="srs-thumbnail-icon" />
    </div>
  );
}

function SuggestedRoutes({ user }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const mapCenter = state?.mapCenter;

  const [itineraries, setItineraries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchItineraries = useCallback(async () => {
    if (!mapCenter) return;
    setLoading(true);
    setError(false);
    try {
      const results = await ApiService.post('/itinerary/search/', {
        location: {
          latitude: mapCenter.lat,
          longitude: mapCenter.lng,
        },
        language: i18n.language,
        radius: 50000,
      }, user);
      setItineraries(results ?? []);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, mapCenter, i18n.language]);

  useEffect(() => {
    fetchItineraries();
  }, [fetchItineraries]);

  const filtered = itineraries.filter(it => {
    const name = it.name?.text ?? '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSelect = (itinerary) => {
    navigate('/homepage', { state: { suggestedItinerary: itinerary } });
  };

  const isSpinning = loading;

  return (
    <div className="srs-container">
      <div className="srs-header">
        <div className="srs-header-title-block">
          <h1 className="srs-title">{t('suggested_routes.title')}</h1>
          <p className="srs-subtitle">{t('suggested_routes.subtitle')}</p>
        </div>
        <button className="srs-menu-btn" onClick={() => navigate('/homepage')}>
          <FaBars />
        </button>
      </div>

      <div className="srs-search-wrapper">
        <FaSearch className="srs-search-icon" />
        <input
          className="srs-search-input"
          type="text"
          placeholder={t('suggested_routes.search_placeholder')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="srs-section-header">
        <span className="srs-section-title">{t('suggested_routes.browse_routes')}</span>
      </div>

      <div className="srs-list">
        {isSpinning ? (
          <div className="srs-loading">
            <FaSpinner className="srs-spinner" />
          </div>
        ) : error ? (
          <p className="srs-empty">{t('suggested_routes.error')}</p>
        ) : filtered.length === 0 ? (
          <p className="srs-empty">{t('suggested_routes.empty')}</p>
        ) : (
          filtered.map(it => {
            const name = it.name?.text ?? t('suggested_routes.unnamed');
            const stops = it.waypoints?.length ?? 0;
            const mins = estimateDuration(stops);
            return (
              <div key={it.id} className="srs-card" onClick={() => handleSelect(it)}>
                <RouteThumbnail />
                <div className="srs-card-info">
                  <span className="srs-card-title">{name}</span>
                  <span className="srs-card-meta">
                    {mins} {t('suggested_routes.mins')} &bull; {stops} {t('suggested_routes.stops')}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SuggestedRoutes;
